import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useContext, useMemo } from 'react';
import { Button, Flex, VStack } from '@chakra-ui/react';
import { hasValue, useT } from '@progress-fe/core';
import { SimpleTable } from '../../tables';
import { IconButton } from '../../buttons';
import { tableDataUtils } from '../../../utils';
import { JsFieldInspector } from '../../jsFormBase';
import { useJsFieldFormData } from '../../../hooks';
import { JsFormConfigContext } from '../../../contexts';
/**
 * Component renders "/schemas/jsf-reaction-equilibrium-const-table" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is an instance of TReactionEquilibriumConstTableFormData.
 */
const ReactionEquilibriumConstTableJsFieldFC = (props) => {
    const { t } = useT();
    const { runCallback, isInspector } = useContext(JsFormConfigContext) || {};
    const { fieldConfig, fieldValues, onChangeFieldValues } = useJsFieldFormData({
        config: props.formData?.fieldConfig || null,
        values: props.formData?.fieldValues || null,
        onChange: props.onChange
    });
    const handleCalcCoefficients = async () => {
        if (!fieldConfig?.callbackCode) {
            return;
        }
        const formDataResult = await runCallback?.(fieldConfig.callbackCode);
        if (hasValue(formDataResult)) {
            onChangeFieldValues(formDataResult.fieldValues);
        }
    };
    const handleAddRow = () => {
        if (!fieldConfig?.columns.length) {
            return;
        }
        const newItem = tableDataUtils.createEmptyItem(fieldConfig.columns);
        const newValue = [...(fieldValues || []), newItem];
        onChangeFieldValues(newValue);
    };
    const handleRemoveRow = useCallback((deleteId) => {
        if (!fieldValues) {
            return;
        }
        const newValue = tableDataUtils.removeValueAtId(fieldValues, deleteId);
        onChangeFieldValues(newValue);
    }, [fieldValues, onChangeFieldValues]);
    const handleValueChange = (rowId, newValue) => {
        if (!fieldValues) {
            return;
        }
        const newTableData = tableDataUtils.changeValueById(fieldValues, newValue, rowId);
        onChangeFieldValues(newTableData);
    };
    const colums = useMemo(() => {
        if (!fieldConfig?.columns) {
            return [];
        }
        return [
            ...fieldConfig.columns,
            {
                name: '',
                width: '28px',
                sticky: true,
                render: (rowId) => (_jsx(IconButton, { icon: "Cross", onClick: () => handleRemoveRow(rowId) }))
            }
        ];
    }, [fieldConfig?.columns, handleRemoveRow]);
    const rows = useMemo(() => (fieldValues || []).map((value, idx) => ({
        key: idx,
        value
    })), [fieldValues]);
    const isReadOnly = !!props.schema.readOnly;
    return (_jsx(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['object'], children: _jsxs(VStack, { "data-testid": "ReactionEquilibriumConstTableJsField-test", "data-name": props.name, pb: "2px", gap: "8px", children: [_jsx(SimpleTable, { columns: colums, rows: rows, noItemsTitle: t('common.noData'), onValueChange: handleValueChange }), _jsxs(Flex, { w: "100%", gap: "4px", alignItems: "center", justifyContent: "stretch", children: [_jsx(Button, { variant: "ghost", size: "smSquare", width: "100%", isDisabled: isReadOnly, onClick: handleAddRow, children: t('actions.add') }), _jsx(Button, { variant: "ghost", size: "smSquare", width: "100%", isDisabled: isReadOnly || rows.length < 4, onClick: handleCalcCoefficients, children: t('actions.calcCoefficients') })] })] }) }));
};
export const ReactionEquilibriumConstTableJsField = memo(ReactionEquilibriumConstTableJsFieldFC);
//# sourceMappingURL=ReactionEquilibriumConstTableJsField.js.map